// pages/admin/EditBlogPage.tsx
import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Col, FormGroup, Label, Row, Spinner } from "reactstrap";
import * as Yup from "yup";
import { getBlogByIdAPI, updateBlogAPI } from "../../../../../api/blogAPIs.ts"; // Adjust path
import { getAllCategories } from "../../../../../api/categoryAPIs.js"; // Adjust path
import { PreviewCard } from "../../../../../components/Component";
import Content from "../../../../../layout/content/Content";
import Head from "../../../../../layout/head/Head";

const EditBlogPage = () => {
    const { id } = useParams();
    // const [categories, setCategories] = useState([]);
    const [files, setFiles] = useState([]);
    const [initialBlog, setInitialBlog] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate()

    const categories = [
        { _id: "healthy-recipes", name: "Healthy Recipes & Nutrition" },
        { _id: "himalayan-lifestyle", name: "Himalayan Lifestyle & Superfoods" },
        { _id: "natural-wellness", name: "Natural Wellness & Ayurveda" },
        { _id: "product-education", name: "Product Education & Guides" },
        { _id: "pahadi-culture", name: "Pahadi Culture & Festivals" },
        { _id: "sustainable-living", name: "Sustainable Living" },
        { _id: "women-empowerment", name: "Women Empowerment Stories" },
        { _id: "news-announcements", name: "News & Announcements" },
    ];


    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [categoriesResponse, blogResponse] = await Promise.all([
                    getAllCategories(),
                    getBlogByIdAPI({ id }),
                ]);
                // setCategories(categoriesResponse.data.data);
                const blogData = blogResponse.data.data;
                setInitialBlog(blogData);
                if (blogData.imageUrl) {
                    setFiles([{ preview: blogData.imageUrl, name: "Existing Image" }]);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                toast.error("Failed to fetch data.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    const handleDropChange = (acceptedFiles, setFieldValue) => {
        setFiles(
            acceptedFiles.map((file) => Object.assign(file, { preview: URL.createObjectURL(file) }))
        );
        setFieldValue("imageUrl", acceptedFiles);
    };

    const initialValues = initialBlog
        ? {
            title: initialBlog.title,
            content: initialBlog.content,
            category: initialBlog.category,
            sequence: initialBlog.sequence,
            isPinned: initialBlog.isPinned,
            isHidden: initialBlog.isHidden,
            tags: initialBlog.tags.join(", "),
            imageUrl: initialBlog.imageUrl ? [new File([], "existing")] : null,
        }
        : {
            title: "",
            content: "",
            category: "",
            sequence: 0,
            isPinned: false,
            isHidden: false,
            tags: "",
            imageUrl: null,
        };

    const validationSchema = Yup.object({
        title: Yup.string().required("Blog title is required"),
        content: Yup.string()
            .required("Content is required")
            .test("is-empty", "Content is required", (value) => {
                const strippedValue = value.replace(/<[^>]+>/g, "").trim();
                return strippedValue.length > 0;
            }),
        category: Yup.string().required("Category is required"),
        sequence: Yup.number()
            .required("Sequence is required")
            .integer("Sequence must be an integer")
            .min(0, "Sequence cannot be negative"),
        tags: Yup.string(),
        imageUrl: Yup.mixed()
            .test("fileType", "Only .jpeg, .png, .jpg are accepted", (value) =>
                !value || value.every((file) => ["image/jpeg", "image/png", "image/jpg"].includes(file.type) || file.name === "existing")
            ),
    });

    const handleSubmit = async (values, { resetForm }) => {
        setLoading(true);
        try {
            const formData = new FormData();
            const blogData = {
                ...values,
                tags: values.tags.split(",").map((tag) => tag.trim()).filter((tag) => tag),
            };
            formData.append("data", JSON.stringify(blogData));
            if (files.length && files[0].name !== "existing") {
                files.forEach((file) => formData.append("images", file));
            }

            const response = await updateBlogAPI({ id, formData });
            if (!response.data.success) throw new Error(response.data.message);

            toast.success("Blog updated successfully");
            navigate("/blogs");
        } catch (error) {
            console.error("Error updating blog:", error);
            toast.error(error.message || "Failed to update blog");
        } finally {
            setLoading(false);
        }
    };

    const modules = {
        toolbar: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link", "image"],
            ["clean"],
        ],
    };

    const formats = ["header", "bold", "italic", "underline", "strike", "list", "bullet", "link", "image"];

    if (!initialBlog && !loading) {
        return (
            <React.Fragment>
                <Head title="Edit Blog" />
                <Content className="container mt-5">
                    <PreviewCard>
                        <h3>Blog Not Found</h3>
                        <Button color="primary" onClick={() => navigate("/admin/blogs")}>
                            Back to Blog List
                        </Button>
                    </PreviewCard>
                </Content>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Head title={`Edit Blog - ${initialBlog?._id}`} />
            <Content className="container mt-5">
                <PreviewCard>
                    <h3>Edit Blog</h3>
                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner color="primary" />
                        </div>
                    ) : (
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                        >
                            {({ setFieldValue, values }) => (
                                <Form>
                                    <Row className="g-3">
                                        <Col sm="12">
                                            <Label>Blog Title</Label>
                                            <Field
                                                name="title"
                                                className="form-control"
                                                placeholder="Enter blog title"
                                            />
                                            <ErrorMessage name="title" component="div" className="text-danger" />
                                        </Col>

                                        <Col sm="12">
                                            <Label>Content</Label>
                                            <ReactQuill
                                                theme="snow"
                                                value={values.content}
                                                onChange={(value) => setFieldValue("content", value)}
                                                modules={modules}
                                                formats={formats}
                                                placeholder="Enter blog content"
                                                className={`${!values.content ? "is-invalid" : ""}`}
                                            />
                                            <ErrorMessage name="content" component="div" className="text-danger" />
                                        </Col>

                                        <Col sm="6">
                                            <Label>Category</Label>
                                            <Field as="select" name="category" className="form-control" >
                                                <option value="">Select Category</option>
                                                {categories.map((category) => (
                                                    <option key={category._id} value={category._id}>
                                                        {category.name}
                                                    </option>
                                                ))}
                                            </Field>
                                            <ErrorMessage name="category" component="div" className="text-danger" />
                                        </Col>

                                        <Col sm="6">
                                            <Label>Sequence</Label>
                                            <Field
                                                name="sequence"
                                                type="number"
                                                className="form-control"
                                                placeholder="Enter sequence"
                                            />
                                            <ErrorMessage name="sequence" component="div" className="text-danger" />
                                        </Col>

                                        <Col sm="12">
                                            <FormGroup check className="mb-3">
                                                <Label check>
                                                    <Field
                                                        type="checkbox"
                                                        name="isPinned"
                                                        className="form-check-input"
                                                    />{" "}
                                                    Pin Blog
                                                </Label>
                                            </FormGroup>
                                            <FormGroup check className="mb-3">
                                                <Label check>
                                                    <Field
                                                        type="checkbox"
                                                        name="isHidden"
                                                        className="form-check-input"
                                                    />{" "}
                                                    Hide Blog
                                                </Label>
                                            </FormGroup>
                                        </Col>

                                        <Col sm="12">
                                            <Label>Tags (comma-separated)</Label>
                                            <Field
                                                name="tags"
                                                className="form-control"
                                                placeholder="Enter tags (e.g., tech, news)"
                                            />
                                            <ErrorMessage name="tags" component="div" className="text-danger" />
                                        </Col>

                                        <Col sm="12">
                                            <Label>Image</Label>
                                            <Dropzone
                                                onDrop={(acceptedFiles) => handleDropChange(acceptedFiles, setFieldValue)}
                                                accept="image/jpeg, image/png, image/jpg"
                                                maxFiles={1}
                                            >
                                                {({ getRootProps, getInputProps, isDragActive }) => (
                                                    <section>
                                                        <div
                                                            {...getRootProps()}
                                                            className={`dropzone p-3 border-2 ${isDragActive ? "border-primary" : "border-dashed"} rounded`}
                                                        >
                                                            <input {...getInputProps()} />
                                                            <p className="text-muted text-center">
                                                                Drag 'n' drop an image here, or click to select
                                                            </p>
                                                            <p className="text-center text-secondary">
                                                                Accepted formats: .jpeg, .png, .jpg | Max 1 file
                                                            </p>
                                                        </div>
                                                    </section>
                                                )}
                                            </Dropzone>
                                            <div className="d-flex flex-wrap mt-3">
                                                {files.map((file, index) => (
                                                    <div
                                                        key={file.name}
                                                        className="position-relative m-2"
                                                        style={{ width: 100, height: 100 }}
                                                    >
                                                        <img
                                                            src={file.preview}
                                                            alt="preview"
                                                            className="rounded img-fluid border"
                                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                        />
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            className="position-absolute top-0 end-0"
                                                            onClick={() => {
                                                                const newFiles = [...files];
                                                                newFiles.splice(index, 1);
                                                                setFiles(newFiles);
                                                                setFieldValue("imageUrl", newFiles);
                                                            }}
                                                        >
                                                            ×
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                            <ErrorMessage name="imageUrl" component="div" className="text-danger" />
                                        </Col>

                                        <Col sm="12">
                                            <div className="d-flex justify-content-end">
                                                <Button color="secondary" onClick={() => navigate("/admin/blogs")}>
                                                    Cancel
                                                </Button>
                                                <Button
                                                    color="primary"
                                                    type="submit"
                                                    className="ms-1"
                                                    disabled={loading}
                                                >
                                                    {loading ? <Spinner size="sm" /> : "Update Blog"}
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                                </Form>
                            )}
                        </Formik>
                    )}
                </PreviewCard>
            </Content>
        </React.Fragment>
    );
};

export default EditBlogPage;